import React, { useEffect, useMemo, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { get, isEmpty } from 'lodash';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { Listings } from 'containers/AppContainer/typings';
import {
  CorporealDocument,
  JudgementADDDocumentType,
} from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import messages from 'containers/common/LegalProceedings/JudgementAdd/Recap/messages';
import { translate } from 'containers/services/translate';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import SuccessDialog from 'components/SuccessDialog';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import {
  ExpertReportType,
  IVehiculeExpertise,
  IVictimExpertise,
} from 'containers/common/LegalProceedings/ExpertReport/typings';
import { PROCEDURE_TYPES } from 'containers/LawyerLegalProceedingsPage/constants';
import ExpertReportAppeal from 'containers/common/LegalProceedings/ExpertReportAppeal/index';
import { Container } from 'containers/LawyerLegalProceedingsPage/ExpertReport/styled-components';
import { useFormatExpertReportState } from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/hooks';
import {
  extractExpertReport,
  getDeactivationButton,
} from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/utils';
import { medicalExpertisesValidator } from 'containers/common/LegalProceedings/ExpertReport/FormsByExpertiseType/form-validations';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import UploadDocumentBloc from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/UploadDocumentBloc';
import { DOCUMENT_TYPES } from 'constants/global';
import {
  EMPTY_SUPPORT_DOC,
  SUPPORT_DOC_EXPERT_REPORT_ID,
} from 'containers/LawyerRecapPage/constants';
import Document from 'components/Document';
import { handleOpenDocument } from 'services/utils';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import { STATUS_PENDING } from 'containers/LawyerFeeNote/constants';

export interface LawyerExpertReportAppealProps {
  intl: IntlShape;
  judgementAddDocuments: JudgementADDDocumentType[];
  listings: Listings;
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadedDocument: Function;
  documentId: string;
  setDocumentId: Function;
  requestCreateExpertiseRaport: Function;
  claimId: number;
  claimNumber: string;
  requestGetExpertiseRaport: Function;
  expertiseReport: ExpertReportType;
  readOnly?: boolean;
  isManager?: boolean;
  judiciaryProcessId: number;
  requestAddExpertiseSupportDocument?: Function;
  requestValidateExpertReport?: Function;
  currentJudiciaryProcess?: any;
  isAssignedLawyer: boolean;
  assignedLawyers: string[];
  sagisVictimInfo: any;
  sagisOpposingParties: any;
  policyInfo: any;
  requestGetDocByDocumentumId: Function;
}

export const LawyerExpertReportAppeal: React.FC<LawyerExpertReportAppealProps> = ({
  ...props
}): JSX.Element => {
  const [isSuccessPopinOpen, setIsSuccessPopinOpen] = React.useState<boolean>(
    false,
  );

  const expertiseSupportDocuments = get(
    props.expertiseReport,
    'supportDocuments',
    [],
  ).filter(
    (el: any) =>
      el.type === DOCUMENT_TYPES.SUPPORT_DOCUMENT_APPEAL_EXPERT_REPORT,
  );

  const [expertReportState, setExpertReportState] = useFormatExpertReportState(
    props.judgementAddDocuments,
    props.expertiseReport,
  );
  const { victimExpertises = [], vehiculeExpertises = [] } = useMemo(
    () =>
      extractExpertReport(
        expertReportState,

        (v: IVictimExpertise) => !isEmpty(v.victimExpertise),

        (v: IVehiculeExpertise) => !isEmpty(v.vehiculeExpertise),
      ),
    [expertReportState, props.expertiseReport],
  );

  const [deactivateButton, setDeactivateButton] = useState([] as any);
  useEffect(() => {
    return setDeactivateButton(
      getDeactivationButton(victimExpertises, vehiculeExpertises),
    );
  }, [victimExpertises, vehiculeExpertises]);
  let validateVictimExpertise = {};
  if (victimExpertises[0]) {
    validateVictimExpertise = medicalExpertisesValidator(victimExpertises[0]);
  }

  const handleClick = () => {
    pushTo(PAGES.LAWYER_HOME)();
  };

  React.useEffect(() => {
    props.requestGetExpertiseRaport(
      props.claimId,
      PROCEDURE_TYPES.APPEAL,
      props.judiciaryProcessId,
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.claimId, props.judiciaryProcessId]);

  const [supportDocument, setSupportDocument] = useState({
    ...EMPTY_SUPPORT_DOC,
  });

  const setExpertiseSupportDocument = (document: CorporealDocument) => {
    if (SUPPORT_DOC_EXPERT_REPORT_ID === props.documentId) {
      setSupportDocument(document);
      props.requestAddExpertiseSupportDocument &&
        props.requestAddExpertiseSupportDocument({
          ...document,
          expertReportId: get(props.expertiseReport, 'id'),
          claimId: props.claimId,
          isLawyer: true,
          claimNumber: props.claimNumber,
          isInboxesActive: featureService.hasFeature(
            FEATURES.ATTRIBUTION_INBOXES_MANAGER,
          ),
          status: STATUS_PENDING,
        });
      props.resetUploadedDocument();
    }
  };
  const isCreator =
    get(props.expertiseReport, 'creator', '') ===
    currentIntervenantStorage.get()?.preferred_username;
  return (
    <Container id="lawyer-expert-report">
      {(props.isAssignedLawyer || !isEmpty(props.expertiseReport)) && (
        <ExpertReportAppeal
          claimId={props.claimId}
          expertReport={props.expertiseReport}
          listings={props.listings}
          documentId={props.documentId}
          setDocumentId={props.setDocumentId}
          uploadDocument={props.uploadDocument}
          uploadedDocument={props.uploadedDocument}
          resetUploadedDocument={props.resetUploadedDocument}
          expertReportState={expertReportState}
          setExpertReportState={setExpertReportState}
          validateVictimExpertise={validateVictimExpertise}
          isAssignedLawyer={
            !isEmpty(props.expertiseReport) ? isCreator : props.isAssignedLawyer
          }
          judiciaryProcessId={props.judiciaryProcessId}
          isCreator={isCreator}
          deactivateButton={deactivateButton}
          setDeactivateButton={setDeactivateButton}
          judgementAddDocuments={props.judgementAddDocuments}
          setIsSuccessPopinOpen={setIsSuccessPopinOpen}
          victimExpertises={victimExpertises}
          vehicleExpertises={vehiculeExpertises}
          requestCreateExpertiseRaport={props.requestCreateExpertiseRaport}
          claimNumber={props.claimNumber}
          requestGetExpertiseRaport={props.requestGetExpertiseRaport}
          requestValidateExpertReport={props.requestValidateExpertReport}
          isManager={false}
          assignedLawyers={props.assignedLawyers}
          sagisVictimInfo={props.sagisVictimInfo}
          sagisOpposingParties={props.sagisOpposingParties}
          isConsultation={false}
          policyInfo={props.policyInfo}
          imagineDocuments={[]}
          requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
        />
      )}
      {featureService.hasFeature(FEATURES.ADD_SUPPORT_DOCUMENTS) &&
        !isEmpty(props.expertiseReport) &&
        isCreator &&
        isEmpty(expertiseSupportDocuments) && (
          <section className="add-recap-bloc">
            {props.resetUploadedDocument &&
              props.uploadDocument &&
              props.uploadedDocument && (
                <>
                  <div className="add-recap-bloc-title">
                    {translate(messages.supportDocument, props)}
                  </div>
                  <UploadDocumentBloc
                    index={1}
                    document={supportDocument}
                    setDocument={setExpertiseSupportDocument}
                    resetUploadedDocument={props.resetUploadedDocument}
                    uploadDocument={props.uploadDocument}
                    uploadedDocument={props.uploadedDocument}
                    docType={
                      DOCUMENT_TYPES.SUPPORT_DOCUMENT_APPEAL_EXPERT_REPORT
                    }
                    setDocumentId={props.setDocumentId}
                    docId={SUPPORT_DOC_EXPERT_REPORT_ID}
                  />
                </>
              )}
          </section>
        )}
      {!isEmpty(expertiseSupportDocuments) && (
        <section>
          <div className="support-document-info">
            <Document
              name={get(expertiseSupportDocuments[0], 'name', '')}
              type={get(expertiseSupportDocuments[0], 'type', '')}
              icon={
                <VisibilityIcon
                  className="consult"
                  onClick={handleOpenDocument(
                    expertiseSupportDocuments[0] as any,
                    props.requestGetDocByDocumentumId,
                  )}
                />
              }
            />
          </div>
        </section>
      )}
      <SuccessDialog
        title={translate(messages.successDialogTitle, props)}
        secondaryButtonTitle={translate(
          messages.successDialogSecondaryButtonTitle,
          props,
        )}
        open={isSuccessPopinOpen}
        onClose={setIsSuccessPopinOpen}
        homePath={PAGES.LAWYER_HOME}
        isExpertReport={true}
        onPrimaryClick={handleClick}
      />
    </Container>
  );
};

export default injectIntl(LawyerExpertReportAppeal);
